import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { catchError, finalize, forkJoin, of, switchMap, tap } from 'rxjs';
import { UserAuth } from 'src/app/authentication/profile/models/user-profile';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import {
    InputFormDialogComponent,
    InputFormDialogData,
} from 'src/app/shared/components/input-form-modal/input-form-dialog.component';
import { ToastMessageService } from 'src/app/shared/services/toast-message.service';
import {
    OrganizationMemberDto,
    OrganizationMemberPage,
    OrganizationMemberRole,
    OrganizationMemberStatus,
} from '../../model/organization-member';
import {
    OrganizationService,
    OrganizationInvitationRequestDto,
} from '../../services/organization.service';
import { validationPatterns } from 'src/app/shared/validation-patterns';
import { TranslateService } from '@ngx-translate/core';

interface OrganizationMemberListEntry {
    id: string;
    email: string;
    displayName: string;
    role: OrganizationMemberRole;
    status: string;
    type: string;
    membershipType: string;
}

interface InviteDialogValues {
    firstName: string;
    lastName: string;
    email: string;
}

@Component({
    selector: 'app-organization-members-page',
    templateUrl: './organization-members-page.component.html',
    styleUrls: ['./organization-members-page.component.scss'],
    standalone: false,
})
export class OrganizationMembersPageComponent implements OnInit {
    private static readonly TOAST_MESSAGE_DELAY = { delay: 2000 };
    private static readonly BASE_PAGE_SIZE = 10;
    /** padding-top 4vh + picture 8vh */
    private static readonly AREA_HEADER_HEIGHT_VH = 0.12;

    organizationId: string = '';
    isLoading = false;
    canManageInvitations = false;
    canManageRoles = false;
    memberListEntries: OrganizationMemberListEntry[] = [];
    readonly organizationMemberRoles = Object.values(OrganizationMemberRole);
    page = 1;
    pageSize = OrganizationMembersPageComponent.BASE_PAGE_SIZE;
    maxSize = 3;
    collectionSize = 0;
    invitationActionsMenuIndex = -1;
    seats: number | undefined = undefined;
    private readonly roleChangeInProgressMemberIds = new Set<string>();

    constructor(
        private authenticationService: AuthenticationService,
        private organizationService: OrganizationService,
        private matDialog: MatDialog,
        private messageService: ToastMessageService,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.pageSize = this.calculatePageSizeFromScreenHeight();
        this.authenticationService
            .observableUserAuth()
            .pipe(
                switchMap((userAuth: UserAuth) => {
                    const organizationIds = Object.keys(
                        userAuth?.organizationProperties ?? {}
                    );
                    this.organizationId = userAuth?.activeOrganization
                        ? userAuth.activeOrganization
                        : (organizationIds[0] ?? '');

                    if (this.organizationId === '') {
                        this.memberListEntries = [];
                        this.canManageInvitations = false;
                        this.canManageRoles = false;
                        return of(null);
                    }

                    this.canManageInvitations =
                        this.authenticationService.isAtLeastOrganizationAdmin(
                            this.organizationId
                        );
                    this.canManageRoles =
                        this.authenticationService.isOrganizationSuperAdmin(
                            this.organizationId
                        );

                    if (this.canManageInvitations) {
                        return forkJoin({
                            seats: this.organizationService.getSeats(),
                            memberRefresh: this.loadOrganizationMembers(),
                        });
                    }
                    return this.loadOrganizationMembers();
                })
            )
            .subscribe((responses) => {
                const count = responses?.seats.count;
                if (count === -1) {
                    this.seats = Infinity;
                } else {
                    this.seats = count;
                }
            });
    }

    openInviteDialog(): void {
        if (!this.canManageInvitations) {
            return;
        }

        const data: InputFormDialogData = {
            headerTranslationKey:
                'organization.organizationArea.members.inviteDialog.header',
            submitButtonTranslationKey:
                'organization.organizationArea.members.inviteDialog.submit',
            cancelButtonTranslationKey:
                'organization.organizationArea.members.inviteDialog.cancel',
            inputErrorTranslationKeyPrefix:
                'organization.organizationArea.members.inviteDialog.errors',
            submitAction: (values) =>
                this.sendInvitation(values as unknown as InviteDialogValues),
            resolveErrorTranslationKey: (error: any) => {
                if (error.error.type === 'Conflict') {
                    if (error.error.message.includes('is already invited')) {
                        return 'organization.organizationArea.members.errors.alreadyInvited';
                    }
                    if (error.error.message.includes('is already member')) {
                        return 'organization.organizationArea.members.errors.alreadyMember';
                    }
                }
                return 'default.errorMessage';
            },
            fields: [
                {
                    controlName: 'email',
                    labelTranslationKey:
                        'organization.organizationArea.members.inviteDialog.email',
                    validators: [Validators.required, Validators.email],
                    maxLength: 255,
                },
                {
                    controlName: 'firstName',
                    labelTranslationKey:
                        'organization.organizationArea.members.inviteDialog.firstName',
                    validators: [
                        Validators.pattern(
                            validationPatterns.extendedNamePattern
                        ),
                    ],
                    maxLength: 20,
                },
                {
                    controlName: 'lastName',
                    labelTranslationKey:
                        'organization.organizationArea.members.inviteDialog.lastName',
                    validators: [
                        Validators.pattern(
                            validationPatterns.extendedNamePattern
                        ),
                    ],
                    maxLength: 20,
                },
            ],
        };

        this.matDialog
            .open(InputFormDialogComponent, { data })
            .afterClosed()
            .pipe(
                switchMap((result) => {
                    if (!result) {
                        return of(null);
                    }

                    this.messageService.showTranslatedSuccessMsg(
                        'organization.organizationArea.members.success.sendInvitation',
                        OrganizationMembersPageComponent.TOAST_MESSAGE_DELAY
                    );
                    return this.loadOrganizationMembers();
                })
            )
            .subscribe();
    }

    onResendInvitation(id: string): void {
        this.organizationService
            .resendOrganizationInvitation(id)
            .pipe(switchMap(() => this.loadOrganizationMembers()))
            .subscribe({
                next: () => {
                    this.messageService.showTranslatedSuccessMsg(
                        'organization.organizationArea.members.success.resendInvitation',
                        OrganizationMembersPageComponent.TOAST_MESSAGE_DELAY
                    );
                    this.invitationActionsMenuIndex = -1;
                },
                error: () => {
                    this.messageService.showTranslatedErrorMsg(
                        'organization.organizationArea.members.errors.resendInvitation',
                        OrganizationMembersPageComponent.TOAST_MESSAGE_DELAY
                    );
                },
            });
    }

    onCancelInvitation(id: string): void {
        this.organizationService
            .cancelOrganizationInvitation(id)
            .pipe(switchMap(() => this.loadOrganizationMembers()))
            .subscribe({
                next: () => {
                    this.messageService.showTranslatedSuccessMsg(
                        'organization.organizationArea.members.success.cancelInvitation',
                        OrganizationMembersPageComponent.TOAST_MESSAGE_DELAY
                    );
                },
                error: () => {
                    this.messageService.showTranslatedErrorMsg(
                        'organization.organizationArea.members.errors.cancelInvitation',
                        OrganizationMembersPageComponent.TOAST_MESSAGE_DELAY
                    );
                },
            });
    }

    isRoleChangeInProgress(memberId: string): boolean {
        return this.roleChangeInProgressMemberIds.has(memberId);
    }

    onRoleChange(
        member: OrganizationMemberListEntry,
        role: OrganizationMemberRole
    ): void {
        if (
            !this.canManageRoles ||
            member.type !== 'member' ||
            member.role === role ||
            this.isRoleChangeInProgress(member.id)
        ) {
            return;
        }

        this.roleChangeInProgressMemberIds.add(member.id);
        this.organizationService
            .updateOrganizationMemberRole(member.id, { role })
            .pipe(
                tap(() => {
                    const entry = this.memberListEntries.find(
                        (item) => item.id === member.id
                    );
                    if (entry) {
                        entry.role = role;
                    }
                    this.messageService.showTranslatedSuccessMsg(
                        'organization.organizationArea.members.success.updateRole',
                        OrganizationMembersPageComponent.TOAST_MESSAGE_DELAY
                    );
                }),
                switchMap(() => this.refreshSessionIfCurrentUser(member)),
                catchError((error) =>
                    this.loadOrganizationMembers().pipe(
                        catchError(() => of(null)),
                        tap(() => {
                            this.messageService.showTranslatedErrorMsg(
                                this.resolveRoleUpdateErrorTranslationKey(
                                    error
                                ),
                                OrganizationMembersPageComponent.TOAST_MESSAGE_DELAY
                            );
                        })
                    )
                ),
                finalize(() => {
                    this.roleChangeInProgressMemberIds.delete(member.id);
                })
            )
            .subscribe();
    }

    getRoleTranslationKey(role: OrganizationMemberRole): string {
        const roleKeyMap: Record<string, string> = {
            [OrganizationMemberRole.ADMIN]:
                'organization.organizationArea.members.roles.admin',
            [OrganizationMemberRole.SUPERADMIN]:
                'organization.organizationArea.members.roles.superAdmin',
            [OrganizationMemberRole.MEMBER]:
                'organization.organizationArea.members.roles.member',
        };
        return roleKeyMap[role];
    }

    getStatusTranslationKey(status: OrganizationMemberStatus): string {
        return (
            'organization.organizationArea.members.status.' +
            status.toLowerCase()
        );
    }

    getSeatsText(): string {
        return (
            this.translateService.instant(
                'organization.organizationArea.members.seats'
            ) +
            ': ' +
            this.collectionSize +
            '/' +
            (Number.isFinite(this.seats) ? this.seats : '∞')
        );
    }

    onPageChange(page: number): void {
        this.page = page;
        this.loadOrganizationMembers().subscribe();
    }
    onOpenInvitationActionsMenu(index: number): void {
        this.invitationActionsMenuIndex = index;
    }

    private resolveRoleUpdateErrorTranslationKey(error: unknown): string {
        if (this.isLastSuperadminConflict(error)) {
            return 'organization.organizationArea.members.errors.lastSuperadmin';
        }
        return 'organization.organizationArea.members.errors.updateRole';
    }

    private isLastSuperadminConflict(error: unknown): boolean {
        return (
            error instanceof HttpErrorResponse &&
            error.status === 409 &&
            typeof error.error?.message === 'string' &&
            error.error.message.includes('LastSuperadminException')
        );
    }

    private refreshSessionIfCurrentUser(member: OrganizationMemberListEntry) {
        const currentUsername =
            this.authenticationService.currentUserValue?.username;
        if (!currentUsername || member.displayName !== currentUsername) {
            return of(null);
        }

        return this.authenticationService
            .refreshUserAuth()
            .pipe(catchError(() => of(null)));
    }

    private loadOrganizationMembers() {
        this.isLoading = true;
        this.invitationActionsMenuIndex = -1;
        return this.organizationService
            .getOrganizationMembers(this.page, this.pageSize)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                }),
                switchMap((response: OrganizationMemberPage) => {
                    this.memberListEntries = response.content.map((entry) =>
                        this.mapBackendEntryToListEntry(entry)
                    );
                    this.collectionSize = response.totalElements;
                    return of(null);
                })
            );
    }

    private sendInvitation(values: InviteDialogValues) {
        const dto: OrganizationInvitationRequestDto = {
            firstName: values.firstName || undefined,
            lastName: values.lastName || undefined,
            email: values.email,
        };
        return this.organizationService.createOrganizationInvitation(dto);
    }

    private calculatePageSizeFromScreenHeight(): number {
        let referenceHeight = 538.5; //50px padding, 38,5 header 390 table entries, 60 actions bar
        let rowHeight = 39;
        let seatLimitSize = 22;
        if (window.innerWidth <= 1300) {
            referenceHeight = 524.03; // 32px padding, 34,33 header 386.7 table entries, 72 actions bar
            rowHeight = 38.67;
            seatLimitSize = 18;
        }
        const tabHeight = 79;
        const margins = 32;
        const usableHeight =
            window.innerHeight *
                (1 - OrganizationMembersPageComponent.AREA_HEADER_HEIGHT_VH) -
            tabHeight -
            margins -
            seatLimitSize;

        return Math.max(
            1,
            Math.floor(
                OrganizationMembersPageComponent.BASE_PAGE_SIZE -
                    (referenceHeight - usableHeight) / rowHeight
            )
        );
    }

    private mapBackendEntryToListEntry(
        entry: OrganizationMemberDto
    ): OrganizationMemberListEntry {
        const type =
            entry.status === OrganizationMemberStatus.PENDING ||
            entry.status === OrganizationMemberStatus.EXPIRED
                ? 'invitation'
                : 'member';
        return {
            ...entry,
            type,
        };
    }
}
